import { VR, fill } from "@/lib/viewingRoom/wording"

// Viewing Room: adapts the public presenters' guest props to the shapes the
// shared hire components read. Guests receive what a signed-in customer sees
// on the row, card, samples and reviews (figures in the customer's form,
// availability included), so nothing here hides data; it renames (slug → id,
// reviewCount → reviews). `penName`
// becomes the server's `displayName` (the public pen name, else the role
// label), so every shared component and every %{pen_name} line has a name.

const MONTH = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric", timeZone: "UTC" })

// The signed-in sort labels, unchanged (CustomerFreelancersPresenter::SORTS);
// the public presenter sends the keys only.
export const GUEST_SORTS = [
  { key: "rating", label: "Highest rated" },
  { key: "reviews", label: "Most reviewed" },
  { key: "reliability", label: "Most reliable" },
  { key: "availability", label: "Soonest available" },
]

// A failed guest visit, in the approved words: the browsing limit says how
// long to wait (the server's retry_after_seconds, else Retry-After);
// anything else gets `fallback`.
export function visitError(response, fallback) {
  if (response?.status !== 429) return { body: fallback }

  let seconds = Number(response.headers?.["retry-after"])
  try {
    const data = typeof response.data === "string" ? JSON.parse(response.data) : response.data
    seconds = Number(data?.retry_after_seconds) || seconds
  } catch {
    // The Retry-After header stands.
  }
  return {
    title: VR.error.browseLimitedTitle,
    body: fill(VR.error.browseLimitedBody, { retry_after_seconds: seconds || 60 }),
  }
}

const shownName = (f) => f.displayName ?? f.penName ?? ""

export function guestCard(card) {
  return { ...card, id: card.slug, penName: shownName(card), reviews: card.reviewCount }
}

// Customer tab labels, unchanged (CustomerFreelancerProfilePresenter#tab_model).
const PORTFOLIO_TAB = {
  snippets: { icon: "file", label: "Content Snippets" },
  audio: { icon: "headphones", label: "Narration Samples" },
}

function portfolioTab(f) {
  const { kind, items } = f.portfolio
  if (kind === "gallery") {
    return { key: "portfolio", icon: "image", label: f.type === "designer" ? "Cover Portfolio" : "Illustrations", count: items.length }
  }
  const tab = PORTFOLIO_TAB[kind]
  return tab ? { key: "portfolio", ...tab, count: items.length } : null
}

// questionLabels: FreelancerQuestion::QUESTIONS, from the server.
export function guestProfile(f, questionLabels = {}) {
  return {
    ...f,
    id: f.slug,
    penName: shownName(f),
    tabs: [
      portfolioTab(f),
      { key: "overview", icon: "user", label: "Overview" },
      { key: "bio", icon: "details", label: "Bio" },
      { key: "niches", icon: "list", label: "Niches & Topics" },
      { key: "reviews", icon: "star", label: "Reviews", count: f.reviews.length },
    ].filter(Boolean),
    overview: {
      headline: f.headline,
      facts: f.overview.map((fact) => ({ key: fact.key, label: questionLabels[fact.key] || "", answer: fact.answer })),
    },
    portfolio: {
      kind: f.portfolio.kind,
      items: f.portfolio.items.map((item) => ({ ...item, niche: item.niche?.label })),
    },
    reviews: f.reviews.map((r) => ({
      by: r.by,
      date: MONTH.format(new Date(`${r.date}-01T00:00:00Z`)),
      rating: r.rating,
      text: r.text,
    })),
    similar: f.similar.map((s) => ({ ...s, id: s.slug, penName: shownName(s), reviews: s.reviewCount })),
  }
}
